import React, { useCallback, useEffect } from 'react';
import { useHistory, useParams } from "react-router-dom";
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { dateTimeFormatter, MenuButton, axiosInstance,  useAuthStore } from '@fides/fides-react-components';
import MenuItem from '@material-ui/core/MenuItem';
import InfoRoundedIcon from '@material-ui/icons/InfoRounded';

import { ApplicationContent } from '../../components/layout/ApplicationContent/ApplicationContent';
import Breadcrumb from '../../components/layout/Breadcrumb/Breadcrumb';
import PageAttribute from '../../components/layout/PageAttribute/PageAttribute';
import PageTitle from '../../components/layout/PageTitle/PageTitle';
import { Ruler } from '../../components/layout/Ruler/Ruler';
import LabelLight from '../../components/typography/LabelLight';
import Headline4 from '../../components/typography/Headline4';
import Headline1 from '../../components/typography/Headline1';
import ViewReportDetails from '../../components/ViewReportDetails/ViewReportDetails';

import { API_ENDPOINTS } from '../../constants/ApiEndpoints';
import { useDatasource } from '../../hooks/useDatasource';
import { Document } from '../../types/Document';
import { DefaultDocument } from '../../constants/DefaultDocument';
import { defaultColDef } from '../../components/Document/DocumentOverview';
import { DLCDataGrid } from '../../components/grid/DLCDataGrid';
import { DataGridStyled } from './styles';
import { checkCSAdmin, checkCSClient, checkFidesAdmin, downloadReport } from "../../api/helper";
import { useFetchStaticData } from "../../api/hooks";
import { useSnackbar } from 'notistack';
import { CPS } from '../../constants/constant';

const InfoRow = styled.div`
    display: flex;
    width: 250px;
`;

const InfoLabel = styled.div`
    width: 50%;
    padding: 9px 16px;
    text-align: left;
`;

const InfoValue = styled.div`
    width: 50%;
    padding: 8px 16px;
    text-align: right;
`;

const MoreInfoContainer = styled.div`
    margin: 0 auto;
    border-left: none !important;
    padding: inherit;
`;

const ViewReport = () => {
    const history = useHistory();
    const params: any = useParams();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [document, setDocument] = React.useState<Document>({ ...DefaultDocument });

    const downloadsDatasource = useDatasource<any>({
        url: API_ENDPOINTS.documents.downloader.findAll,
        params: { filter: { documentUuid: params.uuid } },
    }, t);

    const crumbs = [
        { label: t('documentTitles.reports'), to: '/overviewReports' },
        { label: document.fileName || '' },
    ];

    const columnDefs = [
        {
            headerName: t("document.downloadedOn"),
            field: 'downloadedDateTime',
            valueFormatter: dateTimeFormatter,
            filter: 'agDateColumnFilter',
            filterParams: { filterOptions: null },
            initialSort: 'desc' as const
        },
        {
            headerName: t("document.downloadedBy"),
            field: 'username',
        },
    ];

    const getDocument = useCallback(() => {
        axiosInstance
            .get(`${API_ENDPOINTS.documents.getDocumentById}?uuid=${params.uuid}`)
            .then((response: any) => {
                setDocument(response.data);
            });
    }, [params.uuid]);

    const changeActiveStatus = () => {
        axiosInstance
            .put(`${API_ENDPOINTS.documents.activate}`, {
                uuids: [document.uuid],
                active: !document.active
            })
            .then(() => getDocument());
    };

    const download = (e: React.MouseEvent<HTMLLIElement, MouseEvent>, uuid: string, fileName: string) => {
        e.preventDefault();
        downloadReport(uuid, fileName).then(() => getDocument());
    }

    const deleteDocument = () => {
        const uuids = [document.uuid];
        axiosInstance
            .put(`${API_ENDPOINTS.documents.delete}`, { uuids: uuids })
            .then(() => {
                console.info('Successfully deleted document: ' + uuids[0]);
                history.push("/overviewReports");
            }).catch((reason) => {
                console.error(reason);
                enqueueSnackbar(t('document.onlyInactiveDeleted'), {
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                });
            });
    };

    const user = useAuthStore(state => state.user);
    const isFidesOrCSAdmin = checkFidesAdmin(user) || checkCSAdmin(user);
    const isCSClient = checkCSClient(useFetchStaticData());
    const isCPSDoc = document.application === CPS;
    const canActivateOrDeactivateReport = isCPSDoc && ((!user?.domainUser?.accountGroupUuid && isCSClient) || isFidesOrCSAdmin);

    useEffect(() => {
        getDocument();
    }, [getDocument])

    return (
        <ApplicationContent>
            <Breadcrumb crumbs={crumbs} />
            <PageTitle>
                <PageAttribute
                    attribute={document.fileName}
                    label={t("document.fileName")}
                    isAttributeMain={true}
                />
                <PageAttribute
                    attribute={document.reportType}
                    label={t("document.reportType")}
                />
                <PageAttribute
                    attribute={document.status}
                    label={t("document.downloadStatus")}
                />
                <MoreInfoContainer>
                    <MenuButton
                        aria-label="More"
                        title="More"
                        iconComponent={<InfoRoundedIcon />}
                    >
                        <InfoRow>
                            <InfoLabel><LabelLight>{t("document.fileType")}</LabelLight></InfoLabel>
                            <InfoValue><Headline4>{document.fileType}</Headline4></InfoValue>
                        </InfoRow>
                        <InfoRow>
                            <InfoLabel><LabelLight>{t("document.fileSize")}</LabelLight></InfoLabel>
                            <InfoValue><Headline4>{document.contentSizeKB} {t("document.KB")}</Headline4></InfoValue>
                        </InfoRow>
                        <InfoRow>
                            <InfoLabel><LabelLight>{t("document.reportStatus")}</LabelLight></InfoLabel>
                            <InfoValue><Headline4>{document.active ? t("generic.active") : t("generic.inactive")}</Headline4></InfoValue>
                        </InfoRow>
                    </MenuButton>
                    <MenuButton
                        aria-label={t("generic.more")}
                        title={t("generic.more")}
                    >
                        <MenuItem onClick={e => download(e, document.uuid, document.fileName)}>{t("generic.downloadReport")}</MenuItem>
                        { canActivateOrDeactivateReport && (
                            <MenuItem onClick={changeActiveStatus}>
                                {document.active ? t("generic.inactivateReport") : t("generic.activateReport")}
                            </MenuItem>
                        )}
                        { ((isCPSDoc && !document.active) || (!isCSClient && !isCPSDoc)) && (
                            <MenuItem onClick={deleteDocument}>{t("generic.delete")}</MenuItem>
                        )}
                    </MenuButton>
                </MoreInfoContainer>
            </PageTitle>
            <Ruler />
            <PageTitle>
                <Headline1>{t("documentTitles.reportDetails")}</Headline1>
            </PageTitle>
            <ViewReportDetails document={document} />
            <br />
            <Ruler />
            <PageTitle>
                <Headline1>{t("documentTitles.downloads")}</Headline1>
            </PageTitle>
            <DataGridStyled>
                <DLCDataGrid
                    gridId='dlcDownloaderOverviewGrid'
                    defaultColDef={defaultColDef}
                    columnDefs={columnDefs}
                    cacheBlockSize={10}
                    gridOptions={{ paginationPageSize: 10, serverSideStoreType: 'partial' }}
                    datasource={downloadsDatasource}
                    showFilterRowInitially={false}
                    suppressRowSelection
                />
            </DataGridStyled>
        </ApplicationContent>
    );
};

export default ViewReport;